import { apiErrorMessage, customFetch } from '@agos/api-client'
import { useGetCentralBranding } from '@agos/api-client/platform'
import {
  T,
  formatCents,
  formatCount,
  formatDateTime,
  formatDay,
  formatPercent,
  restaurantNameOf,
  tt,
  useTheme,
} from '@agos/shared'
import { BUTTON_PRIMARY, ConfirmDialog, DateRangePicker, ReadFailedBlock, Spinner, StatusBadge, cn, useToast } from '@agos/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, HandCoins, Plus, Printer, X } from 'lucide-react'
import { useState } from 'react'

import { CashbackSheet, SHEET_STYLES } from './CashbackSheet'
import {
  EMPTY_FORM,
  ORGANIZATIONS_PATH,
  bucketLabel,
  cashbackRowSummary,
  changePayload,
  createPayload,
  formOf,
  formProblems,
  organizationPath,
  organizationsOf,
  parseEurosToCents,
  payoutPath,
  payoutProblem,
  payoutsPath,
  reportOf,
  reportPath,
  splitSentence,
  thisMonth,
  type CashbackOrganization,
  type OrganizationForm,
  type Payout,
} from './organization-cashback'

const ORGANIZATIONS_KEY = ['cashback-organizations'] as const

/** The organizations, read once for the row and the section under it - one key, so one request. */
function useOrganizations() {
  return useQuery({
    queryKey: ORGANIZATIONS_KEY,
    queryFn: () => customFetch<unknown>(ORGANIZATIONS_PATH),
  })
}

/**
 * *Cashback pre organizácie* as the *Cashback* row of the campaigns table - `FRONTEND.md` §9.785, owner O138g.
 *
 * The owner: the cashback campaign *"mala byť vytvorená inak - je to pre organizácie"*. So it is drawn in
 * *Vernostné odmeny* where the customer cashback was, with a summary a reader needs without opening it - how many
 * organizations and what the restaurant still owes them - and **Spravovať** opens §9.782's section under it.
 *
 * **No switch in the switch column**: each organization has its own, and one for all of them would be a second
 * place deciding whether a code works. And if the retired customer cashback is still switched on somewhere, the
 * row says so and offers to switch it off - its card, which carried that switch, is no longer drawn.
 */
export function OrganizationCashbackRow({
  writable,
  retiredOn,
  switchingOff,
  onSwitchOffRetired,
}: {
  writable: boolean
  /** Whether the customer cashback is still switched on - §9.785. */
  retiredOn: boolean
  switchingOff: boolean
  onSwitchOffRetired: () => void
}) {
  const [open, setOpen] = useState(false)
  const list = useOrganizations()
  const organizations = organizationsOf(list.data)

  return (
    <>
      <tr
        data-organization-cashback-row={open ? 'open' : 'closed'}
        className="border-t border-border hover:bg-muted/30 transition-colors"
      >
        <td className="px-4 py-2.5 align-top">
          <p className="font-semibold flex items-center gap-1.5">
            <HandCoins className="w-3.5 h-3.5 text-primary shrink-0" /> <T>Cashback pre organizácie</T>
          </p>
          <p className="text-xs text-muted-foreground">
            <T>Zákazník s kódom organizácie dostane zľavu a organizácii patrí podiel z jedla jeho objednávky.</T>
          </p>
          {!list.isLoading && (
            <p className="text-xs text-muted-foreground" data-organization-cashback-summary={list.isError ? 'failed' : 'read'}>
              {cashbackRowSummary(organizations, list.isError)}
            </p>
          )}
          {retiredOn && (
            <div className="mt-1 flex flex-wrap items-start gap-2 text-xs text-amber-500" data-retired-cashback-on>
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span className="flex-1 min-w-0">
                <T>
                  Starý cashback pre zákazníka je stále zapnutý - po platbe online vracia zákazníkovi časť sumy
                  kupónom, hoci sa tu už ako kampaň nezobrazuje.
                </T>
              </span>
              {writable && (
                <button
                  type="button"
                  data-retired-cashback-off
                  disabled={switchingOff}
                  onClick={onSwitchOffRetired}
                  className="px-2.5 py-1 rounded-lg border border-amber-500/40 font-bold hover:bg-amber-500/10 disabled:opacity-50"
                >
                  <T>Vypnúť</T>
                </button>
              )}
            </div>
          )}
        </td>
        <td className="px-4 py-2.5 hidden lg:table-cell" />
        <td className="px-4 py-2.5 hidden md:table-cell" />
        <td className="px-4 py-2.5 hidden md:table-cell" />
        <td className="px-4 py-2.5 align-top text-center text-[11px] text-muted-foreground" data-organization-cashback-switch>
          <T>pre každú organizáciu</T>
        </td>
        <td className="px-4 py-2.5 align-top text-right">
          <button
            type="button"
            data-organization-cashback-open
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
            className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold hover:bg-muted"
          >
            {open ? <T>Zavrieť</T> : <T>Spravovať</T>}
          </button>
        </td>
      </tr>
      {open && (
        <tr data-organization-cashback-panel>
          <td colSpan={6} className="px-4 pb-4 pt-1">
            <OrganizationCashbackSection writable={writable} />
          </td>
        </tr>
      )}
    </>
  )
}

/**
 * The organizations under their row - `FRONTEND.md` §9.782, `BACKEND.md` §10A.436, owner O138g; in the row since
 * §9.785.
 *
 * The organizations, each with its one code and how it splits an order; what each has earned, been paid
 * and is still owed; and a report per organization of the orders behind the figure, with the payouts the
 * restaurant has recorded.
 */
export function OrganizationCashbackSection({ writable }: { writable: boolean }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [editing, setEditing] = useState<CashbackOrganization | 'new' | null>(null)
  const [reporting, setReporting] = useState<number | null>(null)

  const list = useOrganizations()
  const organizations = organizationsOf(list.data)

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_KEY })

  return (
    <section className="rounded-xl border border-border bg-background/60 p-4 space-y-3" data-organization-cashback>
      <div className="flex items-center justify-end gap-3 flex-wrap">
        <button
          type="button"
          data-organization-new
          disabled={!writable || editing !== null}
          onClick={() => setEditing('new')}
          className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs disabled:opacity-50', BUTTON_PRIMARY)}
        >
          <Plus className="w-3.5 h-3.5" /> <T>Nová organizácia</T>
        </button>
      </div>
      <p className="text-xs text-muted-foreground" data-organization-explained>
        <T>
          Jeden kód kupónu pre všetkých podporovateľov, napríklad futbalového tímu: zákazník dostane zľavu a
          organizácii patrí podiel z jedla každej objednávky, ktorá sa už nedá zrušiť. Reštaurácia jej ho vypláca
          sama - tu sa eviduje, koľko jej patrí a koľko už dostala.
        </T>
      </p>

      {editing !== null && (
        <OrganizationEditor
          organization={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(created) => {
            setEditing(null)
            refresh()
            toast({ title: created ? tt('Organizácia pridaná') : tt('Organizácia uložená') })
          }}
        />
      )}

      {list.isError ? (
        <ReadFailedBlock
          marker="read"
          of="cashback-organizations"
          title={tt('Organizácie sa nepodarilo načítať')}
          onRetry={() => void list.refetch()}
        >
          <T>Prázdny zoznam by znamenal, že žiadna organizácia nemá kód ani nič nezarobila - a to nevieme.</T>
        </ReadFailedBlock>
      ) : list.isLoading ? (
        <Spinner />
      ) : organizations.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-organizations-empty>
          <T>Zatiaľ žiadna organizácia.</T>
        </p>
      ) : (
        <div className="space-y-2">
          {organizations.map((organization) => (
            <div
              key={organization.id}
              className="rounded-xl border border-border p-3 space-y-2"
              data-organization={organization.couponCode}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="space-y-0.5">
                  <p className="text-sm font-bold flex items-center gap-2 flex-wrap">
                    {organization.name}
                    <span
                      className="font-mono text-xs px-1.5 py-0.5 rounded bg-muted"
                      data-organization-code={organization.couponCode}
                    >
                      {organization.couponCode}
                    </span>
                    <StatusBadge
                      color={organization.active ? 'green' : 'zinc'}
                      label={organization.active ? tt('Zapnutá') : tt('Vypnutá')}
                    />
                  </p>
                  <p className="text-xs text-muted-foreground" data-organization-split>
                    {splitSentence(organization)}
                    {organization.endsOn !== null && (
                      <>
                        {' · '}
                        <T>do</T> {formatDay(organization.endsOn)}
                      </>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-organization-edit={organization.couponCode}
                    disabled={!writable || editing !== null}
                    onClick={() => setEditing(organization)}
                    className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold hover:bg-muted disabled:opacity-50"
                  >
                    <T>Upraviť</T>
                  </button>
                  <button
                    type="button"
                    data-organization-report={organization.couponCode}
                    aria-pressed={reporting === organization.id}
                    onClick={() => setReporting(reporting === organization.id ? null : organization.id)}
                    className="px-3 py-1.5 rounded-lg border border-border text-xs font-bold hover:bg-muted"
                  >
                    <T>Report</T>
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <Figure label={tt('Objednávok')} value={formatCount(organization.orders)} marker="orders" />
                <Figure label={tt('Patrí jej spolu')} value={formatCents(organization.owedCents)} marker="owed" />
                <Figure label={tt('Vyplatené')} value={formatCents(organization.paidCents)} marker="paid" />
                <Figure
                  label={tt('Zostáva vyplatiť')}
                  value={formatCents(organization.balanceCents)}
                  marker="balance"
                  strong
                />
              </div>
              {reporting === organization.id && (
                <OrganizationReport organization={organization} writable={writable} onChanged={refresh} />
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function Figure({ label, value, marker, strong }: { label: string; value: string; marker: string; strong?: boolean }) {
  return (
    <div className="rounded-lg bg-muted/40 px-2.5 py-1.5" data-organization-figure={marker}>
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={strong === true ? 'font-bold tabular-nums' : 'tabular-nums'}>{value}</p>
    </div>
  )
}

/** A new organization, or a change to one. The code is locked once an order has used it. */
function OrganizationEditor({
  organization,
  onClose,
  onSaved,
}: {
  organization: CashbackOrganization | null
  onClose: () => void
  onSaved: (created: boolean) => void
}) {
  const [form, setForm] = useState<OrganizationForm>(organization === null ? EMPTY_FORM : formOf(organization))
  const [failure, setFailure] = useState<string | null>(null)
  const problems = formProblems(form)
  const changes = organization === null ? null : changePayload(organization, form)

  const save = useMutation({
    mutationFn: () =>
      organization === null
        ? customFetch<unknown>(ORGANIZATIONS_PATH, { method: 'POST', body: JSON.stringify(createPayload(form)) })
        : customFetch<unknown>(organizationPath(organization.id), {
            method: 'PATCH',
            body: JSON.stringify(changes ?? {}),
          }),
    onSuccess: () => onSaved(organization === null),
    onError: (caught) => setFailure(apiErrorMessage(caught, tt('Organizáciu sa nepodarilo uložiť.'))),
  })

  const set = (patch: Partial<OrganizationForm>) => {
    setFailure(null)
    setForm((current) => ({ ...current, ...patch }))
  }
  const box = 'w-full min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs'

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2" data-organization-editor>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold">
          {organization === null ? tt('Nová organizácia') : organization.name}
        </p>
        <button type="button" aria-label={tt('Zavrieť')} onClick={onClose} data-organization-cancel>
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="text-[11px] text-muted-foreground space-y-0.5">
          <span className="font-semibold">
            <T>Názov organizácie</T>
          </span>
          <input data-organization-field="name" value={form.name} onChange={(event) => set({ name: event.target.value })} className={box} />
        </label>
        <label className="text-[11px] text-muted-foreground space-y-0.5">
          <span className="font-semibold">
            <T>Kód kupónu</T>
          </span>
          <input
            data-organization-field="couponCode"
            value={form.couponCode}
            disabled={organization?.used === true}
            onChange={(event) => set({ couponCode: event.target.value.toUpperCase() })}
            className={`${box} font-mono uppercase disabled:opacity-60`}
          />
          {organization?.used === true && (
            <span className="block text-[10px]" data-organization-code-locked>
              <T>Kód sa už použil na objednávke - zmeniť ho nejde. Pre nový kód pridajte novú organizáciu.</T>
            </span>
          )}
        </label>
        <label className="text-[11px] text-muted-foreground space-y-0.5">
          <span className="font-semibold">
            <T>Zľava pre zákazníka (%)</T>
          </span>
          <input
            data-organization-field="customerPercent"
            inputMode="decimal"
            value={form.customerPercent}
            onChange={(event) => set({ customerPercent: event.target.value })}
            className={box}
          />
        </label>
        <label className="text-[11px] text-muted-foreground space-y-0.5">
          <span className="font-semibold">
            <T>Pre organizáciu (% z jedla)</T>
          </span>
          <input
            data-organization-field="organizationPercent"
            inputMode="decimal"
            value={form.organizationPercent}
            onChange={(event) => set({ organizationPercent: event.target.value })}
            className={box}
          />
        </label>
        <label className="text-[11px] text-muted-foreground space-y-0.5">
          <span className="font-semibold">
            <T>Posledný deň (nepovinné)</T>
          </span>
          <input
            type="date"
            data-organization-field="endsOn"
            value={form.endsOn}
            onChange={(event) => set({ endsOn: event.target.value })}
            className={box}
          />
        </label>
        <label className="flex items-center gap-2 text-xs pt-4">
          <input
            type="checkbox"
            data-organization-field="active"
            checked={form.active}
            onChange={(event) => set({ active: event.target.checked })}
          />
          <T>Kód platí</T>
        </label>
        <label className="text-[11px] text-muted-foreground space-y-0.5 sm:col-span-2">
          <span className="font-semibold">
            <T>Poznámka</T>
          </span>
          <input data-organization-field="note" value={form.note} onChange={(event) => set({ note: event.target.value })} className={box} />
        </label>
      </div>
      {organization !== null && (
        /* An order keeps the percentage it was placed under, so a change reaches only the orders after it. */
        <p className="text-[10px] text-muted-foreground" data-organization-percent-note>
          <T>Zmena percent platí pre ďalšie objednávky - tie, ktoré už kód použili, si nechajú svoje.</T>
        </p>
      )}
      {problems.length > 0 && (
        <p className="text-[11px] text-red-500" data-organization-invalid>
          {problems.join(' ')}
        </p>
      )}
      {failure !== null && (
        <p className="text-[11px] text-red-500" data-organization-failed>
          {failure}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          data-organization-save
          disabled={save.isPending || problems.length > 0 || (changes !== null && Object.keys(changes).length === 0)}
          onClick={() => save.mutate()}
          className={cn('px-3 py-1.5 rounded-lg text-xs disabled:opacity-50', BUTTON_PRIMARY)}
        >
          <T>Uložiť</T>
        </button>
        <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg border border-border text-xs">
          <T>Zrušiť</T>
        </button>
      </div>
    </div>
  )
}

/** The orders behind an organization's figure in a range, and the ledger of what it was paid. */
function OrganizationReport({
  organization,
  writable,
  onChanged,
}: {
  organization: CashbackOrganization
  writable: boolean
  onChanged: () => void
}) {
  const queryClient = useQueryClient()
  const [range, setRange] = useState(() => thisMonth(new Date()))
  const [amount, setAmount] = useState('')
  const [paidOn, setPaidOn] = useState('')
  const [note, setNote] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [removing, setRemoving] = useState<Payout | null>(null)
  /* §9.783 - the handover sheet carries the franchise's own name (§9.700) and when it was printed, fixed when the
     panel opened so a re-render does not move it (§9.317). */
  const { restaurantName: designedName } = useTheme()
  const { data: branding } = useGetCentralBranding()
  const restaurantName = restaurantNameOf(designedName, branding)
  const [printedOn] = useState(() =>
    new Date().toLocaleString('sk-SK', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
  )

  const key = ['cashback-organizations', organization.id, 'report', range.from, range.to] as const
  const report = useQuery({
    queryKey: key,
    queryFn: () => customFetch<unknown>(reportPath(organization.id, range.from, range.to)),
    enabled: range.from !== '' && range.to !== '' && range.from <= range.to,
  })
  const answer = reportOf(report.data)

  const afterWrite = () => {
    void queryClient.invalidateQueries({ queryKey: ['cashback-organizations', organization.id] })
    onChanged()
  }

  const pay = useMutation({
    mutationFn: () =>
      customFetch<unknown>(payoutsPath(organization.id), {
        method: 'POST',
        body: JSON.stringify({
          amount: (parseEurosToCents(amount) ?? 0) / 100,
          paidOn: paidOn === '' ? null : paidOn,
          note,
        }),
      }),
    onSuccess: () => {
      setAmount('')
      setNote('')
      setFailure(null)
      afterWrite()
    },
    onError: (caught) => setFailure(apiErrorMessage(caught, tt('Výplatu sa nepodarilo zaznamenať.'))),
  })

  const remove = useMutation({
    mutationFn: (payout: Payout) =>
      customFetch<unknown>(payoutPath(organization.id, payout.id), { method: 'DELETE' }),
    onSuccess: () => {
      setRemoving(null)
      afterWrite()
    },
    onError: (caught) => {
      setRemoving(null)
      setFailure(apiErrorMessage(caught, tt('Výplatu sa nepodarilo odstrániť.')))
    },
  })

  const problem = amount.trim() === '' ? null : payoutProblem(amount, organization.balanceCents)

  return (
    <div className="space-y-3 border-t border-border pt-3" data-organization-report-panel={organization.couponCode}>
      <div className="flex flex-wrap items-end justify-between gap-2">
        <DateRangePicker
          from={range.from}
          to={range.to}
          onFromChange={(from) => setRange((current) => ({ ...current, from }))}
          onToChange={(to) => setRange((current) => ({ ...current, to }))}
        />
        {/* §9.783 - offered only once the report has answered: a sheet printed from a failed or unfinished read
            would put noughts on a page handed to somebody outside the business. */}
        <button
          type="button"
          data-cashback-print
          disabled={answer === null || report.isError}
          onClick={() => window.print()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-bold hover:bg-muted disabled:opacity-50"
        >
          <Printer className="w-3.5 h-3.5" /> <T>Tlačiť výkaz</T>
        </button>
      </div>
      {report.isError ? (
        <ReadFailedBlock
          marker="read"
          of="cashback-report"
          title={tt('Report sa nepodarilo načítať')}
          onRetry={() => void report.refetch()}
        >
          {apiErrorMessage(report.error, tt('Server na túto otázku neodpovedal - čísla preto nie sú nula, ale neznáme.'))}
        </ReadFailedBlock>
      ) : answer === null ? (
        report.isLoading ? <Spinner /> : null
      ) : (
        <>
          <style>{SHEET_STYLES}</style>
          <CashbackSheet report={answer} restaurantName={restaurantName} printedOn={printedOn} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs" data-report-buckets>
            {(['owed', 'pending', 'cancelled'] as const).map((kind) => (
              <div key={kind} className="rounded-lg border border-border px-2.5 py-2" data-report-bucket={kind}>
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{bucketLabel(kind)}</p>
                <p className="font-bold tabular-nums" data-report-share={kind}>
                  {kind === 'cancelled' ? '-' : formatCents(answer[kind].shareCents)}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {formatCount(answer[kind].orders)} <T>objednávok</T> · <T>jedlo</T>{' '}
                  {formatCents(answer[kind].foodCents)}
                </p>
              </div>
            ))}
          </div>
          {answer.orders.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-report-empty>
              <T>V tomto období kód nepoužila žiadna objednávka.</T>
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-report-orders>
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-2 font-semibold">
                      <T>Objednávka</T>
                    </th>
                    <th className="py-1 pr-2 font-semibold">
                      <T>Čas</T>
                    </th>
                    <th className="py-1 pr-2 font-semibold text-right">
                      <T>Jedlo</T>
                    </th>
                    <th className="py-1 pr-2 font-semibold text-right">%</th>
                    <th className="py-1 pr-2 font-semibold text-right">
                      <T>Podiel</T>
                    </th>
                    <th className="py-1 font-semibold">
                      <T>Stav</T>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {answer.orders.map((order) => (
                    <tr key={order.orderId} className="border-t border-border" data-report-order={order.bucket}>
                      <td className="py-1 pr-2">#{order.orderNumber}</td>
                      <td className="py-1 pr-2">{order.createdAt === '' ? '-' : formatDateTime(order.createdAt)}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{formatCents(order.foodCents)}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{formatPercent(order.organizationPercent)}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">
                        {order.bucket === 'cancelled' ? '-' : formatCents(order.shareCents)}
                      </td>
                      <td className="py-1">{bucketLabel(order.bucket)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {answer.truncated && (
            <p className="text-[11px] text-amber-500" data-report-truncated>
              <T>Zoznam ukazuje prvých 2 000 objednávok - súčty hore zahŕňajú všetky.</T>
            </p>
          )}

          <div className="space-y-1.5" data-organization-payouts>
            <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              <T>Výplaty</T>
            </p>
            {answer.payouts.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                <T>Zatiaľ nič nevyplatené.</T>
              </p>
            ) : (
              answer.payouts.map((payout) => (
                <div key={payout.id} className="flex items-center justify-between gap-2 text-xs" data-payout={payout.id}>
                  <span>
                    {formatDay(payout.paidOn)} · <strong className="tabular-nums">{formatCents(payout.amountCents)}</strong>
                    {payout.note !== '' && <span className="text-muted-foreground"> · {payout.note}</span>}
                    {payout.recordedBy !== '' && <span className="text-muted-foreground"> · {payout.recordedBy}</span>}
                  </span>
                  <button
                    type="button"
                    data-payout-remove={payout.id}
                    disabled={!writable || remove.isPending}
                    onClick={() => setRemoving(payout)}
                    className="text-[11px] underline disabled:opacity-50"
                  >
                    <T>Odstrániť</T>
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}

      <div className="flex flex-wrap items-end gap-2" data-payout-form>
        <label className="text-[11px] text-muted-foreground space-y-0.5">
          <span className="font-semibold">
            <T>Vyplatené (€)</T>
          </span>
          <input
            data-payout-amount
            inputMode="decimal"
            value={amount}
            disabled={!writable}
            onChange={(event) => {
              setFailure(null)
              setAmount(event.target.value)
            }}
            className="w-28 min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
          />
        </label>
        <label className="text-[11px] text-muted-foreground space-y-0.5">
          <span className="font-semibold">
            <T>Dňa</T>
          </span>
          <input
            type="date"
            data-payout-day
            value={paidOn}
            disabled={!writable}
            onChange={(event) => setPaidOn(event.target.value)}
            className="min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
          />
        </label>
        <label className="text-[11px] text-muted-foreground space-y-0.5 flex-1 min-w-[8rem]">
          <span className="font-semibold">
            <T>Poznámka</T>
          </span>
          <input
            data-payout-note
            value={note}
            disabled={!writable}
            onChange={(event) => setNote(event.target.value)}
            className="w-full min-h-[32px] rounded-lg border border-border bg-background px-2 text-xs"
          />
        </label>
        <button
          type="button"
          data-payout-record
          disabled={!writable || pay.isPending || amount.trim() === '' || problem !== null}
          onClick={() => pay.mutate()}
          className={cn('px-3 py-1.5 rounded-lg text-xs disabled:opacity-50', BUTTON_PRIMARY)}
        >
          <T>Zaznamenať výplatu</T>
        </button>
      </div>
      {problem !== null && (
        <p className="text-[11px] text-red-500" data-payout-invalid>
          {problem}
        </p>
      )}
      {failure !== null && (
        <p className="text-[11px] text-red-500" data-payout-failed>
          {failure}
        </p>
      )}

      <ConfirmDialog
        open={removing !== null}
        title={tt('Odstrániť výplatu?')}
        description={
          removing === null
            ? ''
            : `${formatCents(removing.amountCents)} ${tt('zo dňa')} ${formatDay(removing.paidOn)} - ${tt('zostatok organizácie sa o túto sumu zvýši.')}`
        }
        confirmLabel={tt('Odstrániť')}
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (removing !== null) remove.mutate(removing)
        }}
        onClose={() => setRemoving(null)}
      />
    </div>
  )
}
